import { useEffect, useRef, useState } from 'react';
import { assignFormValues } from '../../../Utils/Auth';
import {
  getICDPanelsIndex,
  getComorbiditiesPanelsIndex,
  getSpecimenSourceIndex,
  panelsArrItemRemoval,
  panelsArrMakerToSend,
} from '../../../Utils/Common/Requisition';
import useLang from './../../hooks/useLanguage';
import PanelNamesModal from './PanelNamesModal';
import Collapse from '@mui/material/Collapse';

// Helper function to convert YYYY-MM-DD to MM/DD/YYYY
const formatDateToMMDDYYYY = (dateString: string): string => {
  if (!dateString) return '';
  // Check if already in MM/DD/YYYY format
  if (dateString.includes('/')) {
    return dateString;
  }
  // Convert from YYYY-MM-DD to MM/DD/YYYY
  const [year, month, day] = dateString.split('-');
  if (year && month && day) {
    return `${month}/${day}/${year}`;
  }
  return dateString;
};

// Helper function to convert MM/DD/YYYY to YYYY-MM-DD (for HTML date input)
const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return '';
  // Check if already in YYYY-MM-DD format
  if (dateString.includes('-') && dateString.length === 10) {
    return dateString;
  }
  // Convert from MM/DD/YYYY to YYYY-MM-DD
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return dateString;
};

const HeaderSelectableOnlyPanel = (props: any) => {
  // Track the open/close state of each panel
  const [openPanels, setOpenPanels] = useState(
    props?.panels.reduce((acc: any, panel: any) => {
      acc[panel.panelID] = false; // Initialize each panel as closed
      return acc;
    }, {})
  );
  const [openAOESections, setOpenAOESections] = useState<{
    [key: string]: boolean;
  }>({});
  // Toggle the panel's state (open/close)
  const togglePanel = (panelId: any) => {
    setOpenPanels((prevState: any) => ({
      ...prevState,
      [panelId]: !prevState[panelId], // Toggle the specific panel
    }));
  };
  const togglePanelForChecked = (panelId: any) => {
    setOpenPanels((prevState: any) => ({
      ...prevState,
      [panelId]: true, // Toggle the specific panel
    }));
  };
  const togglePanelForUnChecked = (panelId: any) => {
    setOpenPanels((prevState: any) => ({
      ...prevState,
      [panelId]: false, // Toggle the specific panel
    }));
  };
  const { t } = useLang();
  const [panelsArrToSend, setPanelsArrToSend] = useState<any>([]);
  useEffect(() => {
    setpanelsArrToSendForEdit();
  }, [props.panels]);

  function FindIndex(arr: any[], rid: any) {
    return arr.findIndex((i: any) => i.reqId === rid);
  }
  const toggleAOESection = (panelId: string) => {
    setOpenAOESections((prevState: any) => ({
      ...prevState,
      [panelId]: !prevState[panelId],
    }));
  };
  const parsePanelAOES = (panelAOES: string) => {
    try {
      if (!panelAOES || panelAOES.trim() === "") return null;
      return JSON.parse(panelAOES);
    } catch (error) {
      console.error("Error parsing panelAOES:", error);
      return null;
    }
  };
  // const validateAOEQuestions = (panel: any, panelId: string) => {
  //   if (!panel || !panel.panelAOES) return;

  //   try {
  //     const aoeData = JSON.parse(panel.panelAOES);
  //     props.setAOEIDErrors((prevErrors: any) => {
  //       const errors: any = { ...prevErrors };

  //       aoeData.forEach((question: any) => {
  //         const questionCode = question.QuestionCode || `AOE_${panelId}_${question.QuestionId}`;

  //         // Check if question is required and answer is empty
  //         if (!question.Answer || question.Answer.trim() === "") {
  //           errors[questionCode] = `${question.Question} is required`;
  //         } else {
  //           // Clear error if answer is provided
  //           if (errors[questionCode]) {
  //             delete errors[questionCode];
  //           }
  //         }
  //       });

  //       return errors;
  //     });
  //   } catch (error) {
  //     console.error("Error validating AOE questions:", error);
  //   }
  // };

  const clearAOEFieldsAndErrors = (panelId: string) => {
    let inputsCopy = JSON.parse(JSON.stringify(props?.Inputs));
    const panelToUpdate = inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels?.find(
      (p: any) => p.panelID === panelId
    );
    if (!panelToUpdate) return;

    let hasAOEChanges = false;

    // Clear AOE at panel level
    if (panelToUpdate.panelAOES && panelToUpdate.panelAOES.trim() !== "") {
      try {
        const aoeData = JSON.parse(panelToUpdate.panelAOES);
        let aoeDataChanged = false;

        aoeData.forEach((question: any) => {
          const questionCode = question.QuestionCode || `AOE_${panelId}_${question.QuestionId}`;

          // Clear the answer
          if (question.Answer) {
            question.Answer = "";
            question.IsSelectedAOEs = false;
            aoeDataChanged = true;
          }
        });

        // Update panelAOES if answers were cleared
        if (aoeDataChanged) {
          panelToUpdate.panelAOES = JSON.stringify(aoeData);
          panelToUpdate.isAOEsSelected = false;
          hasAOEChanges = true;
        }
      } catch (error) {
        console.error("Error clearing AOE fields:", error);
      }
    }

    // Clear errors for this panel
    props.setAOEIDErrors((prevErrors: any) => {
      const errors: any = { ...prevErrors };
      Object.keys(errors).forEach((key) => {
        if (key.startsWith(`AOE_${panelId}_`)) {
          delete errors[key];
        }
      });
      return errors;
    });

    // Update the inputs if AOE fields were cleared
    if (hasAOEChanges) {
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        panelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );

      newInputs?.then((res) => {
        let infectiousDataCopy = JSON.parse(JSON.stringify(props?.infectiousData));
        infectiousDataCopy[FindIndex(props?.infectiousData, props?.ArrayReqId)].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    }
  };

  const handleAOEAnswerChange = (
    panelId: string,
    questionId: number,
    answerId: number,
    value: string
  ) => {
    let inputsCopy = JSON.parse(JSON.stringify(props?.Inputs));
    const panel = inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels?.find(
      (p: any) => p.panelID === panelId
    );
    if (!panel || !panel.panelAOES) return;

    try {
      const aoeData = JSON.parse(panel.panelAOES);
      const questionIndex = aoeData.findIndex((q: any) => q.QuestionId === questionId);
      if (questionIndex === -1) return;

      // AOEsAnswers is read-only, only used to display options
      // Only update the question's Answer field
      aoeData[questionIndex].Answer = value;
      aoeData[questionIndex].IsSelectedAOEs = value !== null && value !== "";

      // Update panelAOES string
      panel.panelAOES = JSON.stringify(aoeData);
      panel.isAOEsSelected = aoeData.some((q: any) => q.IsSelectedAOEs);

      // Validate AOE questions after answer change
      // validateAOEQuestions(panel, panelId); // Validation now happens only on preview
      clearAOEFieldsAndErrors(panelId);
      // Update the inputs
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        panelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );

      newInputs?.then((res) => {
        let infectiousDataCopy = JSON.parse(JSON.stringify(props?.infectiousData));
        infectiousDataCopy[FindIndex(props?.infectiousData, props?.ArrayReqId)].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    } catch (error) {
      console.error("Error updating AOE answer:", error);
    }
  };
  const handleAOEMultipleSelectionChange = (
    panelId: string,
    questionId: number,
    answerId: number,
    value: string,
    isChecked: boolean
  ) => {
    let inputsCopy = JSON.parse(JSON.stringify(props?.Inputs));
    const panel = inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels?.find(
      (p: any) => p.panelID === panelId
    );
    if (!panel || !panel.panelAOES) return;

    try {
      const aoeData = JSON.parse(panel.panelAOES);
      const questionIndex = aoeData.findIndex((q: any) => q.QuestionId === questionId);
      if (questionIndex === -1) return;

      // Parse existing answers (comma-separated string or array)
      // AOEsAnswers is read-only, only used to display options
      let selectedAnswers: string[] = [];
      if (aoeData[questionIndex].Answer) {
        if (typeof aoeData[questionIndex].Answer === 'string') {
          selectedAnswers = aoeData[questionIndex].Answer.split(',').map((a: string) => a.trim()).filter((a: string) => a !== '');
        } else if (Array.isArray(aoeData[questionIndex].Answer)) {
          selectedAnswers = [...aoeData[questionIndex].Answer];
        }
      }

      // Add or remove the selected value (store exactly as it comes from AOEsAnswers)
      if (isChecked) {
        if (!selectedAnswers.includes(value)) {
          selectedAnswers.push(value);
        }
      } else {
        selectedAnswers = selectedAnswers.filter((a: string) => a !== value);
      }

      // Update the answer as comma-separated string (store values exactly as they come)
      aoeData[questionIndex].Answer = selectedAnswers.join(',');
      aoeData[questionIndex].IsSelectedAOEs = selectedAnswers.length > 0;
      if (Array.isArray(aoeData[questionIndex].AOEsAnswers)) {
        aoeData[questionIndex].AOEsAnswers = aoeData[questionIndex].AOEsAnswers.map((answer: any) => {
          const answerValue = answer?.Answer !== null && answer?.Answer !== undefined && answer?.Answer !== ""
            ? answer.Answer
            : (answer?.AnswerCode || answer?.AnswerId?.toString() || "");
          const isSelectedAnswer =
            selectedAnswers.includes(answer?.Answer) ||
            selectedAnswers.includes(answerValue) ||
            selectedAnswers.includes(answer?.AnswerCode) ||
            selectedAnswers.includes(answer?.AnswerId?.toString());
          return {
            ...answer,
            IsSelected: !!isSelectedAnswer,
          };
        });
      }

      // Update panelAOES string
      panel.panelAOES = JSON.stringify(aoeData);
      panel.isAOEsSelected = aoeData.some((q: any) => q.IsSelectedAOEs);

      // Validate AOE questions after answer change
      // validateAOEQuestions(panel, panelId); // Validation now happens only on preview
      clearAOEFieldsAndErrors(panelId);
      // Update the inputs
      let newInputs = assignFormValues(
        inputsCopy,
        props?.index,
        props?.depControlIndex,
        props?.fieldIndex,
        panelsArrToSend,
        props?.isDependency,
        props?.repeatFieldSection,
        props?.isDependencyRepeatFields,
        props?.repeatFieldIndex,
        props?.repeatDependencySectionIndex,
        props?.repeatDepFieldIndex,
        undefined,
        props?.setInputs
      );

      newInputs?.then((res) => {
        let infectiousDataCopy = JSON.parse(JSON.stringify(props?.infectiousData));
        infectiousDataCopy[FindIndex(props?.infectiousData, props?.ArrayReqId)].sections = res;
        props?.setInfectiousData([...infectiousDataCopy]);
      });
    } catch (error) {
      console.error("Error updating AOE multiple selection answer:", error);
    }
  };
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [panelNames, setPanelNames] = useState([]); // State to store the panel names

  const handleShowModal = (panelNames: any) => {
    setPanelNames(panelNames); // Set panel names when showing modal
    setShowModal(true); // Show the modal
  };

  const handleCloseModal = () => {
    setShowModal(false); // Hide the modal
  };
  const handleChangeParent = (
    panelID: any,
    panelName: string,
    checked: boolean,
    index: number
  ) => {
    props.fields.enableRule = '';
    const name = panelName;
    let panelsArrToAppendCopy = [...panelsArrToSend];
    let inputsCopy = JSON?.parse(JSON?.stringify(props?.Inputs));
    let PanelIdRemove;
    props.Inputs[props.index].fields[props?.fieldIndex].panels[
      index
    ].isSelected = true;
    props.Inputs[props.index].fields[props?.fieldIndex].panels[
      index
    ].testOptions.forEach((testOptions: any) => {
      testOptions.isSelected = true;
    });
    // Auto-expand AOE section if panelAOES exists
    const currentPanelInitial = props.Inputs[props.index].fields[props?.fieldIndex].panels[index];
    if (currentPanelInitial.panelAOES && currentPanelInitial.panelAOES.trim() !== "") {
      setOpenAOESections((prev: any) => ({
        ...prev,
        [panelID]: true,
      }));
      // Validate AOE questions when panel is selected
      // validateAOEQuestions(currentPanelInitial, panelID); // Validation now happens only on preview
    }
    if (props.panelCombinations.length == 0) {
      if (checked) {
        togglePanelForChecked(panelID);
        let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
        let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
        let ComorbiditiesPanelIndex = getComorbiditiesPanelsIndex(inputsCopy);
        if (specimenSourceIndex) {
          inputsCopy[specimenSourceIndex]?.fields?.forEach(
            (specimenSourceFields: any) => {
              specimenSourceFields?.specimenSources?.forEach(
                (panelsInfo: any) => {
                  panelsInfo.isVisible = false;
                  panelsInfo.specimenSourceOption.forEach((option: any) => {
                    option.isSelected = false;
                  });
                }
              );
            }
          );
        }
        const updatedInputValueForSpecimen =
          props?.inputValueForSpecimen?.filter(
            (option: any) => option.panelID.toString() == panelID.toString()
          );
        props.setInputValueForSpecimen(
          updatedInputValueForSpecimen
        );
        if (ICDPanelIndex) {
          inputsCopy[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
            if (IcdPanelsFields.systemFieldName == 'ICDPanels') {
              IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
                panelsInfo.isVisible = false; // Set all ICDPanels to false initially
              });
            }
          });
        }
        // if (ComorbiditiesPanelIndex) {
        //   inputsCopy[ComorbiditiesPanelIndex]?.fields?.forEach((ComorbiditiesPanelsFields: any) => {
        //     if (ComorbiditiesPanelsFields.systemFieldName == 'ComorbidityPanels') {
        //       ComorbiditiesPanelsFields?.panels?.forEach((panelsInfo: any) => {
        //         panelsInfo.isVisible = false; // Set all ComorbidityPanels to false initially
        //       });
        //     }
        //   });
        // }
        // Now set the clicked ICD panel's visibility to true
        if (ICDPanelIndex) {
          inputsCopy[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
            if (IcdPanelsFields.systemFieldName == 'ICDPanels') {
              IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
                if (panelsInfo.panelName == name) {
                  panelsInfo.isVisible = true; // Set clicked ICD panel to true
                }
              });
            }
          });
        }
        // Now set the clicked Comorbidity panel's visibility to true
        if (ComorbiditiesPanelIndex) {
          inputsCopy[ComorbiditiesPanelIndex]?.fields?.forEach((ComorbiditiesPanelsFields: any) => {
            if (ComorbiditiesPanelsFields.systemFieldName == 'ComorbidityPanels') {
              ComorbiditiesPanelsFields?.panels?.forEach((panelsInfo: any) => {
                if (panelsInfo.panelName == name) {
                  panelsInfo.isVisible = true; // Set clicked Comorbidity panel to true
                }
              });
            }
          });
        }
        //checking that only one parent checkbox is selected at same time
        inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels?.forEach(
          (panel: any, i: number) => {
            if (i !== index) {
              panel.isSelected = false;
              PanelIdRemove = panel?.panelID;
              panel?.testOptions?.forEach((testOption: any) => {
                testOption.isSelected = false;
              });
              // Clear AOE fields and errors for unselected panel
              clearAOEFieldsAndErrors(panel.panelID);
              props.setAOEIDErrors({})
            }
          }
        );
        if (PanelIdRemove) {
          let new_Vanished = panelsArrItemRemoval(
            PanelIdRemove,
            panelsArrToAppendCopy
          );
          let newInputs = assignFormValues(
            inputsCopy,
            props?.index,
            props?.depControlIndex,
            props?.fieldIndex,
            new_Vanished,
            props?.isDependency,
            props?.repeatFieldSection,
            props?.isDependencyRepeatFields,
            props?.repeatFieldIndex,
            props?.repeatDependencySectionIndex,
            props?.repeatDepFieldIndex,
            undefined,
            props?.setInputs
          );
          newInputs?.then(res => {
            let infectiousDataCopy = JSON?.parse(
              JSON?.stringify(props?.infectiousData)
            );
            infectiousDataCopy[
              FindIndex(props?.infectiousData, props?.ArrayReqId)
            ].sections = res;
            props?.setInfectiousData([...infectiousDataCopy]);
          });
        }
        //starting of specimen source
        if (specimenSourceIndex) {
          inputsCopy[specimenSourceIndex]?.fields?.forEach(
            (specimenSourceFields: any) => {
              specimenSourceFields?.specimenSources?.forEach(
                (panelsInfo: any) => {
                  if (panelsInfo?.panelName === name) {
                    panelsInfo.isVisible = true;
                  } else {
                    panelsInfo.isVisible = false;
                  }
                }
              );
            }
          );
        }

        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = true;
        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].testOptions.forEach((testOptions: any) => {
          testOptions.isSelected = true;
        });
        // Auto-expand AOE section if panelAOES exists
        const currentPanel = inputsCopy[props.index].fields[props?.fieldIndex].panels[index];
        if (currentPanel.panelAOES && currentPanel.panelAOES.trim() !== "") {
          setOpenAOESections((prev: any) => ({
            ...prev,
            [panelID]: true,
          }));
          // Validate AOE questions when panel is selected
          // validateAOEQuestions(currentPanel, panelID); // Validation now happens only on preview
        }
        let finalisedPanelsArrToSend = panelsArrMakerToSend(
          index,
          inputsCopy[props.index]?.fields[props?.fieldIndex]?.panels[index],
          panelsArrToAppendCopy,
          checked
        );
        setPanelsArrToSend(finalisedPanelsArrToSend);
        let newInputs = assignFormValues(
          inputsCopy,
          props?.index,
          props?.depControlIndex,
          props?.fieldIndex,
          finalisedPanelsArrToSend,
          props?.isDependency,
          props?.repeatFieldSection,
          props?.isDependencyRepeatFields,
          props?.repeatFieldIndex,
          props?.repeatDependencySectionIndex,
          props?.repeatDepFieldIndex,
          undefined,
          props?.setInputs
        );
        newInputs?.then(res => {
          let infectiousDataCopy = JSON?.parse(
            JSON?.stringify(props?.infectiousData)
          );
          infectiousDataCopy[
            FindIndex(props?.infectiousData, props?.ArrayReqId)
          ].sections = res;
          props?.setInfectiousData([...infectiousDataCopy]);
        });
      }

      if (!checked) {
        togglePanelForUnChecked(panelID);
        let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
        let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
        let ComorbiditiesPanelIndex = getComorbiditiesPanelsIndex(inputsCopy);
        inputsCopy[specimenSourceIndex]?.fields.forEach(
          (specimenSourceFields: any) => {
            specimenSourceFields.specimenSources?.forEach((panelsInfo: any) => {
              if (panelsInfo.panelName === name) {
                panelsInfo.isVisible = false;
                panelsInfo.specimenSourceOption.forEach((option: any) => {
                  option.isSelected = false;
                });
              }
            });
          }
        );
        if (ICDPanelIndex) {
          inputsCopy[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
            if (IcdPanelsFields.systemFieldName == 'ICDPanels') {
              IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
                if (panelsInfo.panelName == name) {
                  panelsInfo.isVisible = false; // Set clicked ICD panel to false
                }
              });
            }
          });
        }
        if (ComorbiditiesPanelIndex) {
          inputsCopy[ComorbiditiesPanelIndex]?.fields?.forEach((ComorbiditiesPanelsFields: any) => {
            if (ComorbiditiesPanelsFields.systemFieldName == 'ComorbidityPanels') {
              ComorbiditiesPanelsFields?.panels?.forEach((panelsInfo: any) => {
                if (panelsInfo.panelName == name) {
                  panelsInfo.isVisible = false; // Set clicked Comorbidity panel to false
                }
              });
            }
          });
        }
        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = false;
        // Clear AOE fields and errors for unselected panel
        clearAOEFieldsAndErrors(panelID);
        props.setAOEIDErrors({})

        inputsCopy[props.index].fields[props?.fieldIndex].panels[
          index
        ].testOptions.forEach((testOptions: any) => {
          testOptions.isSelected = false;
        });
        let finalisedPanelsArrToSend = panelsArrItemRemoval(
          panelID,
          panelsArrToAppendCopy
        );
        setPanelsArrToSend(finalisedPanelsArrToSend);
        let newInputs = assignFormValues(
          inputsCopy,
          props?.index,
          props?.depControlIndex,
          props?.fieldIndex,
          finalisedPanelsArrToSend,
          props?.isDependency,
          props?.repeatFieldSection,
          props?.isDependencyRepeatFields,
          props?.repeatFieldIndex,
          props?.repeatDependencySectionIndex,
          props?.repeatDepFieldIndex,
          undefined,
          props?.setInputs
        );
        newInputs?.then(res => {
          let infectiousDataCopy = JSON?.parse(
            JSON?.stringify(props?.infectiousData)
          );
          infectiousDataCopy[
            FindIndex(props?.infectiousData, props?.ArrayReqId)
          ].sections = res;
          props?.setInfectiousData([...infectiousDataCopy]);
        });
      }
    } else {
      if (checked) {
        togglePanelForChecked(panelID);
        // Step 1: Handle panel selection logic
        let ICDPanelIndex = getICDPanelsIndex(props?.Inputs);
        let ComorbiditiesPanelIndex = getComorbiditiesPanelsIndex(props?.Inputs);
        let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
        props.Inputs[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = true;
        props.Inputs[props.index].fields[props?.fieldIndex].panels[
          index
        ].testOptions.forEach((testOptions: any) => {
          testOptions.isSelected = true;
        });
        // Auto-expand AOE section if panelAOES exists
        const currentPanel = props.Inputs[props.index].fields[props?.fieldIndex].panels[index];
        if (currentPanel.panelAOES && currentPanel.panelAOES.trim() !== "") {
          setOpenAOESections((prev: any) => ({
            ...prev,
            [panelID]: true,
          }));
          // Validate AOE questions when panel is selected
          // validateAOEQuestions(currentPanel, panelID); // Validation now happens only on preview
        }
        // Step 2: Make ICD Panels & specimen source visible if applicable
        if (ICDPanelIndex) {
          props.Inputs[ICDPanelIndex]?.fields?.forEach(
            (IcdPanelsFields: any) => {
              if (IcdPanelsFields.systemFieldName == 'ICDPanels') {
                IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
                  if (panelsInfo.panelName == name) {
                    panelsInfo.isVisible = true;
                  }
                });
              }
            }
          );
        }
        if (ComorbiditiesPanelIndex) {
          props.Inputs[ComorbiditiesPanelIndex]?.fields?.forEach(
            (ComorbiditiesPanelsFields: any) => {
              if (ComorbiditiesPanelsFields.systemFieldName == 'ComorbidityPanels') {
                ComorbiditiesPanelsFields?.panels?.forEach((panelsInfo: any) => {
                  if (panelsInfo.panelName == name) {
                    panelsInfo.isVisible = true;
                  }
                });
              }
            }
          );
        }
        if (specimenSourceIndex) {
          props.Inputs[specimenSourceIndex]?.fields?.forEach(
            (specimenSourceFields: any) => {
              specimenSourceFields?.specimenSources?.forEach(
                (panelsInfo: any) => {
                  if (panelsInfo?.panelName === name) {
                    panelsInfo.isVisible = true;
                  }
                }
              );
            }
          );
        }
        const updatedInputValueForSpecimen =
          props?.inputValueForSpecimen?.filter(
            (option: any) => option.panelID.toString() == panelID.toString()
          );
        props.setInputValueForSpecimen(
          updatedInputValueForSpecimen
        );

        // Step 3: Validate panel combination if more than one panel is selected
        const selectedPanels = props.Inputs[props.index].fields[
          props?.fieldIndex
        ].panels.filter((panel: any) => panel.isSelected);

        if (selectedPanels.length > 1) {
          const selectedPanelIDs = selectedPanels.map((panel: any) =>
            parseInt(panel.panelID)
          );
          const allComboPanelIDArrays: any[] = [];
          // Push each parsed panel combination array into allComboPanelIDArrays
          props.panelCombinations.some((combo: any) => {
            const comboPanelIDArrays = JSON.parse(combo.panelCombinationJson);
            allComboPanelIDArrays.push(comboPanelIDArrays);
          });
          // Check if selectedPanelIDs are fully contained in any of the combo arrays
          const isValid = allComboPanelIDArrays.some(
            (comboPanelIDArray: any[]) => {
              const panelIds = comboPanelIDArray.map(
                (panel: any) => panel.PanelId
              );
              return selectedPanelIDs.every((id: any) => panelIds.includes(id));
            }
          );
          // Return true if valid combination is found, otherwise false
          if (!isValid) {
            props.Inputs[props.index].fields[props?.fieldIndex].panels[
              index
            ].isSelected = false;
            props.Inputs[props.index].fields[props?.fieldIndex].panels[
              index
            ].testOptions.forEach((testOptions: any) => {
              testOptions.isSelected = false;
            });
            if (specimenSourceIndex) {
              props.Inputs[specimenSourceIndex]?.fields?.forEach(
                (specimenSourceFields: any) => {
                  specimenSourceFields?.specimenSources?.forEach(
                    (panelsInfo: any) => {
                      if (panelsInfo?.panelName === name) {
                        panelsInfo.isVisible = false;
                      }
                    }
                  );
                }
              );
            }
            if (ICDPanelIndex) {
              props.Inputs[ICDPanelIndex]?.fields?.forEach(
                (IcdPanelsFields: any) => {
                  if (IcdPanelsFields.systemFieldName == 'ICDPanels') {
                    IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
                      if (panelsInfo.panelName == name) {
                        panelsInfo.isVisible = false;
                      }
                    });
                  }
                }
              );
            }
            if (ComorbiditiesPanelIndex) {
              props.Inputs[ComorbiditiesPanelIndex]?.fields?.forEach(
                (ComorbiditiesPanelsFields: any) => {
                  if (ComorbiditiesPanelsFields.systemFieldName == 'ComorbidityPanels') {
                    ComorbiditiesPanelsFields?.panels?.forEach((panelsInfo: any) => {
                      if (panelsInfo.panelName == name) {
                        panelsInfo.isVisible = false;
                      }
                    });
                  }
                }
              );
            }
            const updatedSelectedPanelIDs = selectedPanelIDs.filter(
              (id: any) => id !== parseInt(panelID)
            );
            let matchingArray = findMatchingArrays(
              updatedSelectedPanelIDs,
              allComboPanelIDArrays
            );

            handleShowModal(matchingArray);
            return;
          }
        }
        // Step 4: Update state with valid selection
        let finalisedPanelsArrToSend = panelsArrMakerToSend(
          index,
          props?.Inputs[props.index]?.fields[props?.fieldIndex]?.panels[index],
          panelsArrToAppendCopy,
          checked
        );
        setPanelsArrToSend(finalisedPanelsArrToSend);
        let newInputs = assignFormValues(
          props?.Inputs,
          props?.index,
          props?.depControlIndex,
          props?.fieldIndex,
          finalisedPanelsArrToSend,
          props?.isDependency,
          props?.repeatFieldSection,
          props?.isDependencyRepeatFields,
          props?.repeatFieldIndex,
          props?.repeatDependencySectionIndex,
          props?.repeatDepFieldIndex,
          undefined,
          props?.setInputs
        );
        newInputs?.then(res => {
          let infectiousDataCopy = JSON?.parse(
            JSON?.stringify(props?.infectiousData)
          );
          infectiousDataCopy[
            FindIndex(props?.infectiousData, props?.ArrayReqId)
          ].sections = res;
          props?.setInfectiousData([...infectiousDataCopy]);
        });
      }
      if (!checked) {
        togglePanelForUnChecked(panelID);
        let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
        let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
        let ComorbiditiesPanelIndex = getComorbiditiesPanelsIndex(inputsCopy);
        if (specimenSourceIndex) {
          props?.Inputs[specimenSourceIndex]?.fields.forEach(
            (specimenSourceFields: any) => {
              specimenSourceFields.specimenSources?.forEach(
                (panelsInfo: any) => {
                  if (panelsInfo.panelName === name) {
                    panelsInfo.isVisible = false;
                    panelsInfo.isSelected = false;
                    panelsInfo?.specimenSourceOption?.map((option: any) => {
                      option.isSelected = false;
                    });
                    const updatedInputValueForSpecimen =
                      props?.inputValueForSpecimen?.filter(
                        (option: any) => option.panelID !== panelID.toString()
                      );
                    props.setInputValueForSpecimen(
                      updatedInputValueForSpecimen
                    );
                  }
                }
              );
            }
          );
        }
        if (ICDPanelIndex) {
          props?.Inputs[ICDPanelIndex]?.fields?.forEach(
            (IcdPanelsFields: any) => {
              if (IcdPanelsFields.systemFieldName == 'ICDPanels') {
                IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
                  if (panelsInfo.panelName == name) {
                    panelsInfo.isVisible = false;
                    panelsInfo.isSelected = false;
                  }
                });
              }
            }
          );
        }
        if (ComorbiditiesPanelIndex) {
          props?.Inputs[ComorbiditiesPanelIndex]?.fields?.forEach(
            (ComorbiditiesPanelsFields: any) => {
              if (ComorbiditiesPanelsFields.systemFieldName == 'ComorbidityPanels') {
                ComorbiditiesPanelsFields?.panels?.forEach((panelsInfo: any) => {
                  if (panelsInfo.panelName == name) {
                    panelsInfo.isVisible = false;
                    panelsInfo.isSelected = false;
                  }
                });
              }
            }
          );
        }
        props.Inputs[props.index].fields[props?.fieldIndex].panels[
          index
        ].isSelected = false;
        // Clear AOE fields and errors for unselected panel
        clearAOEFieldsAndErrors(panelID);
        props.setAOEIDErrors({})
        props.Inputs[props.index].fields[props?.fieldIndex].panels[
          index
        ].testOptions.forEach((testOptions: any) => {
          testOptions.isSelected = false;
        });
        let finalisedPanelsArrToSend = panelsArrItemRemoval(
          panelID,
          panelsArrToAppendCopy
        );
        setPanelsArrToSend(finalisedPanelsArrToSend);
        let newInputs = assignFormValues(
          props.Inputs,
          props?.index,
          props?.depControlIndex,
          props?.fieldIndex,
          finalisedPanelsArrToSend,
          props?.isDependency,
          props?.repeatFieldSection,
          props?.isDependencyRepeatFields,
          props?.repeatFieldIndex,
          props?.repeatDependencySectionIndex,
          props?.repeatDepFieldIndex,
          undefined,
          props?.setInputs
        );
        newInputs?.then(res => {
          let infectiousDataCopy = JSON?.parse(
            JSON?.stringify(props?.infectiousData)
          );
          infectiousDataCopy[
            FindIndex(props?.infectiousData, props?.ArrayReqId)
          ].sections = res;
          props?.setInfectiousData([...infectiousDataCopy]);
        });
      }
    }
  };
  function findMatchingArrays(
    selectedIDs: number[],
    allComboPanelIDArrays: any[][]
  ): any[][] {
    const matchingArrays: any[][] = []; // To store all matching combinations
    for (let comboArray of allComboPanelIDArrays) {
      // Extract all PanelIds from the current array
      const panelIDs = comboArray.map((panel: any) => panel.PanelId);
      // Check if all selectedIDs are present in panelIDs
      if (selectedIDs.every(id => panelIDs.includes(id))) {
        matchingArrays.push(comboArray); // Push the array if it's a subset match
      }
    }

    return matchingArrays;
  }

  const setpanelsArrToSendForEdit = () => {
    let panelsCopy = [...props?.panels];
    let filteredPanelsCopy = panelsCopy?.filter(
      (panelsData: any) => panelsData?.isSelected
    );
    filteredPanelsCopy?.forEach((panelData: any) => {
      setOpenPanels((prevState: any) => ({
        ...prevState,
        [panelData.panelID]: true, // Toggle the specific panel
      }));
    });

    let panelNameArr: any = [];
    filteredPanelsCopy?.forEach((panelData: any) => {
      panelNameArr?.push(panelData?.panelName);
    });
    let inputsCopy = [...props?.Inputs];
    let specimenSourceIndex = getSpecimenSourceIndex(inputsCopy);
    inputsCopy[specimenSourceIndex]?.fields?.forEach(
      (specimenSourceFields: any) => {
        specimenSourceFields?.specimenSources?.forEach((panelsInfo: any) => {
          if (panelNameArr?.includes(panelsInfo.panelName)) {
            panelsInfo.isVisible = true;
          }
        });
      }
    );
    let ICDPanelIndex = getICDPanelsIndex(inputsCopy);
    inputsCopy[ICDPanelIndex]?.fields?.forEach((IcdPanelsFields: any) => {
      if (IcdPanelsFields.systemFieldName == 'ICDPanels') {
        IcdPanelsFields?.panels?.forEach((panelsInfo: any) => {
          if (panelNameArr?.includes(panelsInfo.panelName)) {
            panelsInfo.isVisible = true;
          }
        });
      }
    });
    let ComorbiditiesPanelIndex = getComorbiditiesPanelsIndex(inputsCopy);
    if (ComorbiditiesPanelIndex) {
      inputsCopy[ComorbiditiesPanelIndex]?.fields?.forEach((ComorbiditiesPanelsFields: any) => {
        if (ComorbiditiesPanelsFields.systemFieldName == 'ComorbidityPanels') {
          ComorbiditiesPanelsFields?.panels?.forEach((panelsInfo: any) => {
            if (panelNameArr?.includes(panelsInfo.panelName)) {
              panelsInfo.isVisible = true;
            }
          });
        }
      });
    }
    assignFormValues(
      inputsCopy,
      // props?.dependenceyControls,
      props?.index,
      props?.depControlIndex,
      props?.fieldIndex,
      filteredPanelsCopy,
      props?.isDependency,
      props?.repeatFieldSection,
      props?.isDependencyRepeatFields,
      props?.repeatFieldIndex,
      props?.repeatDependencySectionIndex,
      props?.repeatDepFieldIndex,
      undefined,
      props?.setInputs
    );
    setPanelsArrToSend(filteredPanelsCopy);
  };
  const divElement = useRef<HTMLDivElement | null>(null); // Initialize ref for div
  useEffect(() => {
    // Scroll to the div if props.error is present
    if (props.error && divElement.current) {
      divElement.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    props.setErrorFocussedInput && props.setErrorFocussedInput();
  }, [props?.errorFocussedInput]);
  return (
    <>
      {props.error && (
        <div className="form__error">
          <span>{t(props.error)}</span>
        </div>
      )}
      <div id={props?.name} ref={divElement} tabIndex={-1}></div>
      {props?.panels?.map((options: any, index: number) => (
        <>
          {options.isVisible && (
            <div className={`${props?.displayType}`}>
              <div className="row">
                {/* ***************** 1/4 ****************** */}
                <div className="col-lg-12">
                  <div className="card border border-gray-300 mb-3 rounded">
                    <div
                      className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4"
                      onClick={() => togglePanel(options?.panelID)}
                    >
                      <div className="col-12">
                        <div className="form-check form-check-sm form-check-solid col-12">
                          <input
                            className="form-check-input mr-2 h-20px w-20px"
                            type="checkbox"
                            checked={options?.isSelected}
                            onClick={e => e.stopPropagation()} // Prevent header clicks from toggling the checkbox
                            onChange={(e: any) => {
                              handleChangeParent(
                                options?.panelID,
                                options?.panelName,
                                e.target.checked,
                                index
                              );
                            }}
                          />
                          <label className="fw-600">{options?.panelName}</label>
                        </div>
                      </div>
                    </div>

                    <Collapse
                      in={openPanels[options?.panelID]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <div className="card-body py-md-4 py-3 col-12">
                        <div className="row">
                          {Object.entries(
                            options?.testOptions?.reduce(
                              (acc: any, optionsChild: any) => {
                                const { typeOfTest } = optionsChild;
                                if (typeOfTest) {
                                  // Only add to the group if typeOfTest is truthy
                                  if (!acc[typeOfTest]) {
                                    acc[typeOfTest] = []; // Create a new array for each typeOfTest
                                  }
                                  acc[typeOfTest].push(optionsChild); // Add the current test under its typeOfTest category
                                } else {
                                  // Handle null or undefined typeOfTest: put it in a special category
                                  if (!acc['No Type']) {
                                    acc['No Type'] = [];
                                  }
                                  acc['No Type'].push(optionsChild);
                                }
                                return acc;
                              },
                              {}
                            )
                          )
                            .map(([typeOfTest, tests]: any, index: any) => (
                              <div key={index}>
                                {/* Display typeOfTest header only if it's not "No Type" */}
                                {typeOfTest && typeOfTest !== 'No Type' && (
                                  <div className="fw-bold mb-3">
                                    {typeOfTest}
                                  </div>
                                )}

                                {/* Loop through the tests for this typeOfTest */}
                                <div className="row">
                                  {tests
                                    .map(
                                    (optionsChild: any, childIndex: any) => (
                                      <div
                                        key={childIndex}
                                        className={
                                          tests.length > 1
                                            ? 'mb-3 col-xl-4 col-lg-4 col-md-4 col-sm-6'
                                            : 'col-xl-12 col-lg-12 col-md-12 col-sm-12'
                                        }
                                      >
                                        <label className="form-check form-check-sm align-items-start form-check-solid col-12">
                                          <span className="fw-400">
                                            {optionsChild?.testName}
                                          </span>
                                        </label>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                      {/* AOE Questions at Panel Level */}
                      {options?.isSelected && options?.panelAOES && options?.panelAOES.trim() !== "" && (
                        <AOEQuestions
                          panel={options}
                          panelId={options?.panelID}
                          panelName={options?.panelName}
                          openAOESections={openAOESections}
                          toggleAOESection={toggleAOESection}
                          parsePanelAOES={parsePanelAOES}
                          handleAOEAnswerChange={handleAOEAnswerChange}
                          handleAOEMultipleSelectionChange={handleAOEMultipleSelectionChange}
                          AOEIDErrors={props.AOEIDErrors}
                          setAOEIDErrors={props.setAOEIDErrors}
                        />
                      )}
                    </Collapse>
                  </div>

                  <PanelNamesModal
                    panelNames={panelNames}
                    showModal={showModal}
                    handleClose={handleCloseModal}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      ))}
    </>
  );
};

// AOE Questions Component
const AOEQuestions = (props: any) => {
  const {
    panel,
    panelId,
    panelName,
    openAOESections,
    toggleAOESection,
    parsePanelAOES,
    handleAOEAnswerChange,
    handleAOEMultipleSelectionChange,
    AOEIDErrors,
    setAOEIDErrors
  } = props;

  const hasAOES = panel?.panelAOES && panel?.panelAOES.trim() !== "";
  const aoeData = hasAOES ? parsePanelAOES(panel.panelAOES) : null;
  const isAOEOpen = openAOESections[panelId] || false;
  const errorRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const scrolledErrorsRef = useRef<{ [key: string]: boolean }>({});

  // Scroll to error when AOEIDErrors is set
  useEffect(() => {
    if (AOEIDErrors && Object.keys(AOEIDErrors).length > 0 && aoeData) {
      // Find the first question with an error that we haven't scrolled to yet
      const firstErrorQuestion = aoeData.find((question: any) => {
        const questionCode = question.QuestionCode || `AOE_${panelId}_${question.QuestionId}`;
        const hasError = AOEIDErrors[questionCode];
        // Only scroll if error exists and we haven't scrolled to it yet
        return hasError && !scrolledErrorsRef.current[questionCode];
      });

      if (firstErrorQuestion) {
        const questionCode = firstErrorQuestion.QuestionCode || `AOE_${panelId}_${firstErrorQuestion.QuestionId}`;

        // Re-parse to get latest data
        const latestAoeData = parsePanelAOES(panel.panelAOES);
        if (latestAoeData) {
          const currentQuestion = latestAoeData.find((q: any) => {
            const qCode = q.QuestionCode || `AOE_${panelId}_${q.QuestionId}`;
            return qCode === questionCode;
          });

          if (currentQuestion) {
            // Check if field is actually empty (not filled)
            const isFieldEmpty = !currentQuestion.Answer ||
              (typeof currentQuestion.Answer === 'string' && currentQuestion.Answer.trim() === '') ||
              (Array.isArray(currentQuestion.Answer) && currentQuestion.Answer.length === 0);

            // Only scroll if field is empty AND error exists
            if (isFieldEmpty && AOEIDErrors[questionCode]) {
              // Auto-open the AOE section if it's closed
              if (!isAOEOpen) {
                toggleAOESection(panelId);
              }

              // Scroll to the error field after a short delay to ensure the section is open
              setTimeout(() => {
                const errorRef = errorRefs.current[questionCode];
                if (errorRef && AOEIDErrors && AOEIDErrors[questionCode]) {
                  // Final check: verify the error still exists and field is still empty
                  const finalAoeData = parsePanelAOES(panel.panelAOES);
                  if (finalAoeData) {
                    const finalQuestion = finalAoeData.find((q: any) => {
                      const qCode = q.QuestionCode || `AOE_${panelId}_${q.QuestionId}`;
                      return qCode === questionCode;
                    });

                    if (finalQuestion) {
                      const isStillEmpty = !finalQuestion.Answer ||
                        (typeof finalQuestion.Answer === 'string' && finalQuestion.Answer.trim() === '') ||
                        (Array.isArray(finalQuestion.Answer) && finalQuestion.Answer.length === 0);

                      if (isStillEmpty && AOEIDErrors[questionCode]) {
                        errorRef.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });

                        // Mark this error as scrolled to, so it won't scroll again unless error is cleared and set again
                        scrolledErrorsRef.current[questionCode] = true;

                        // Clear the error after scrolling so it can scroll again if error persists on next submit
                        // Delay clearing to ensure error is visible to user first
                        if (setAOEIDErrors) {
                          setTimeout(() => {
                            setAOEIDErrors((prevErrors: any) => {
                              const errors: any = { ...prevErrors };
                              // Only clear if error still exists and field is still empty
                              if (errors[questionCode]) {
                                // Re-check if field is still empty before clearing
                                const checkAoeData = parsePanelAOES(panel.panelAOES);
                                if (checkAoeData) {
                                  const checkQuestion = checkAoeData.find((q: any) => {
                                    const qCode = q.QuestionCode || `AOE_${panelId}_${q.QuestionId}`;
                                    return qCode === questionCode;
                                  });

                                  if (checkQuestion) {
                                    const stillEmpty = !checkQuestion.Answer ||
                                      (typeof checkQuestion.Answer === 'string' && checkQuestion.Answer.trim() === '') ||
                                      (Array.isArray(checkQuestion.Answer) && checkQuestion.Answer.length === 0);

                                    // Only clear if field is still empty (error not fixed)
                                    if (stillEmpty) {
                                      delete errors[questionCode];
                                      // Also clear the scrolled tracking so it can scroll again if error is set again
                                      delete scrolledErrorsRef.current[questionCode];
                                    }
                                  }
                                }
                              }
                              return errors;
                            });
                          }, 5000); // Clear after 3 seconds so error is visible
                        }
                      }
                    }
                  }
                }
              }, 300);
            }
          }
        }
      }
    } else {
      // Reset scrolled tracking when all errors are cleared
      scrolledErrorsRef.current = {};
    }
  }, [AOEIDErrors]);

  if (!hasAOES || !panel?.isSelected || !aoeData) return null;

  return (
    <div className="mt-3 mb-3">
      <div
        className="d-flex align-items-center cursor-pointer"
        onClick={() => toggleAOESection(panelId)}
        style={{ cursor: "pointer" }}
      >
        <i className={`fas fa-chevron-${isAOEOpen ? "down" : "right"} me-2`}></i>
        <span className="fw-600 text-primary">Additional Questions</span>
      </div>
      {isAOEOpen && (
        <div className="mt-3 ps-4 border-start border-2 border-primary">
          {aoeData.map((question: any, qIndex: number) => {
            const questionCode = question.QuestionCode || `AOE_${panelId}_${question.QuestionId}`;
            const errorMessage = AOEIDErrors?.[questionCode];

            return (
              <div
                key={qIndex}
                className="mb-4"
                ref={(el) => {
                  if (el) {
                    errorRefs.current[questionCode] = el;
                  }
                }}
              >
                <div className="form-label fw-bold mb-2 text-gray-700" style={{ userSelect: 'none' }}>
                  {question?.Question}
                  {question?.IsRequiredAnswer && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </div>

                {question.AnswerDataType === "Text" && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter answer"
                    value={question.Answer || ""}
                    onChange={(e) =>
                      handleAOEAnswerChange(
                        panelId,
                        question.QuestionId,
                        question.AOEsAnswers?.[0]?.AnswerId || 0,
                        e.target.value
                      )
                    }
                  />
                )}
                {question.AnswerDataType === "Number" && (
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter number"
                    value={question.Answer || ""}
                    onChange={(e) =>
                      handleAOEAnswerChange(
                        panelId,
                        question.QuestionId,
                        question.AOEsAnswers?.[0]?.AnswerId || 0,
                        e.target.value
                      )
                    }
                  />
                )}
                {question.AnswerDataType === "Date" && (
                  <input
                    type="date"
                    className="form-control"
                    value={formatDateToYYYYMMDD(question.Answer || "")}
                    onChange={(e) => {
                      const formattedDate = formatDateToMMDDYYYY(e.target.value);
                      handleAOEAnswerChange(
                        panelId,
                        question.QuestionId,
                        question.AOEsAnswers?.[0]?.AnswerId || 0,
                        formattedDate
                      );
                    }}
                  />
                )}
                {question.AnswerDataType === "Multiple Choice" && (
                  <div>
                    {question.AOEsAnswers?.map((answer: any, aIndex: number) => {
                      const answerValue = answer.Answer !== null && answer.Answer !== undefined && answer.Answer !== ""
                        ? answer.Answer
                        : (answer.AnswerCode || answer.AnswerId?.toString() || "");
                      const answerLabel = answer.Answer !== null && answer.Answer !== undefined && answer.Answer !== ""
                        ? answer.Answer
                        : (answer.AnswerCode || `Option ${aIndex + 1}`);

                      const isSelected = !!answer?.IsSelected;

                      return (
                        <div key={aIndex} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`aoe_multiselect_${panelId}_${question.QuestionId}_${aIndex}`}
                            checked={!!isSelected}
                            onChange={(e) =>
                              handleAOEMultipleSelectionChange(
                                panelId,
                                question.QuestionId,
                                answer.AnswerId,
                                answer.Answer !== null && answer.Answer !== undefined && answer.Answer !== ""
                                  ? answer.Answer
                                  : answerValue,
                                e.target.checked
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`aoe_multiselect_${panelId}_${question.QuestionId}_${aIndex}`}
                          >
                            {answerLabel}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
                {question.AnswerDataType === "Single Selection" && (
                  <div>
                    {question.AOEsAnswers?.map((answer: any, aIndex: number) => {
                      const answerValue = answer.Answer !== null && answer.Answer !== undefined && answer.Answer !== ""
                        ? answer.Answer
                        : (answer.AnswerCode || answer.AnswerId?.toString() || "");
                      const answerLabel = answer.Answer !== null && answer.Answer !== undefined && answer.Answer !== ""
                        ? answer.Answer
                        : (answer.AnswerCode || `Option ${aIndex + 1}`);

                      const isSelected =
                        question.Answer === answerValue ||
                        question.Answer === answer.Answer;

                      return (
                        <div key={aIndex} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name={`aoe_${panelId}_${question.QuestionId}`}
                            id={`aoe_${panelId}_${question.QuestionId}_${aIndex}`}
                            checked={!!isSelected}
                            onChange={() =>
                              handleAOEAnswerChange(
                                panelId,
                                question.QuestionId,
                                answer.AnswerId,
                                answer.Answer !== null && answer.Answer !== undefined && answer.Answer !== ""
                                  ? answer.Answer
                                  : answerValue
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`aoe_${panelId}_${question.QuestionId}_${aIndex}`}
                          >
                            {answerLabel}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
                {errorMessage && (
                  <div className="text-danger small mb-2">{errorMessage}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HeaderSelectableOnlyPanel;
